import { useEffect, useState } from 'react'

// Height (px) of the on-screen keyboard that the browser has NOT reflected
// into the layout viewport. Zero whenever the platform correctly shrinks
// the layout viewport to match the keyboard (index.html's
// `interactive-widget=resizes-content` honored by Chrome) — in that case
// `window.innerHeight` already excludes the keyboard, so this stays 0 and
// callers applying it as a translateY offset are a no-op.
//
// Non-zero on WebViews that ignore `interactive-widget` (notably the
// Android TWA's bundled System WebView — see
// docs/diagnostics/learn-keyboard-visual-viewport-pan-diagnosis.md §7):
// there `window.innerHeight` stays full-screen while `visualViewport.height`
// shrinks to the region above the keyboard, so the difference is exactly
// the keyboard-covered band that a `position: sticky/fixed; bottom: 0`
// element would otherwise sit behind.
//
// Only the `resize` event is used — `visualViewport.scroll` fires during
// visual-viewport panning unrelated to the keyboard and produced noisy
// intermediate values in the equivalent Learn-page hook (see that file's
// viewportH effect), so it's deliberately not subscribed here either.
//
// **Only consumer left is DeckCreateModal.tsx.** The deck-feed composer
// (DeckMobileFeed.tsx) stopped using this hook — the on-device measurement
// pass in
// docs/diagnostics/composer-keyboard-positioning-measurement-2026-07-26.md
// found `INSET` from this exact formula was consistently 0 on the affected
// platform (the layout viewport already resizes) while the composer still
// overshot, because the real defect was `position: sticky` capped by its
// scroll-container's containing block, not a keyboard-inset error. The
// composer now uses `useViewportBottomGap` to position a `position: fixed`
// element directly instead of compensating a sticky one. DeckCreateModal's
// panel has the same sticky-adjacent transform pattern and was NOT
// remeasured or touched by that pass — flagged as needing its own
// measurement before a similar fix, not assumed fixed by association.
export function useKeyboardInset(): number {
  const [inset, setInset] = useState(0)

  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) return

    const update = () => {
      const covered = window.innerHeight - vv.height - vv.offsetTop
      setInset(Math.max(0, Math.round(covered)))
    }
    update()
    vv.addEventListener('resize', update)
    return () => vv.removeEventListener('resize', update)
  }, [])

  return inset
}
